import { useLayoutEffect } from 'react';

// Every route's static build HTML already carries a server-rendered
// (`data-ssr="true"`) title/meta/canonical/og/twitter tag (baked by
// tools/generate_route_html.py) plus a static `og:type` default in
// public/index.html. If a page also RENDERS these as JSX, React 19's native
// head-hoisting creates a brand new DOM node for each one — it has no idea
// the server-rendered node already exists — so bots that execute JS (the
// production live-rendering proxy) see two of every tag after hydration.
//
// Fix: pages stop rendering these 12 tags as JSX entirely and call this hook
// instead. It finds the existing element already in <head> by its stable
// selector and updates its text/attribute in place; it only creates a new
// element on the rare page that has none to find. No element is ever
// created when one already exists, so no duplicate is ever written — and
// the surviving tag is always the original server-rendered one.
const TAG_CONFIG = {
  title: { selector: 'title', create: () => document.createElement('title') },
  description: { selector: 'meta[name="description"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('name', 'description'); return el; } },
  canonical: { selector: 'link[rel="canonical"]', attr: 'href', create: () => { const el = document.createElement('link'); el.setAttribute('rel', 'canonical'); return el; } },
  ogTitle: { selector: 'meta[property="og:title"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('property', 'og:title'); return el; } },
  ogDescription: { selector: 'meta[property="og:description"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('property', 'og:description'); return el; } },
  ogUrl: { selector: 'meta[property="og:url"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('property', 'og:url'); return el; } },
  ogType: { selector: 'meta[property="og:type"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('property', 'og:type'); return el; } },
  ogImage: { selector: 'meta[property="og:image"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('property', 'og:image'); return el; } },
  twitterCard: { selector: 'meta[name="twitter:card"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('name', 'twitter:card'); return el; } },
  twitterTitle: { selector: 'meta[name="twitter:title"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('name', 'twitter:title'); return el; } },
  twitterDescription: { selector: 'meta[name="twitter:description"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('name', 'twitter:description'); return el; } },
  twitterImage: { selector: 'meta[name="twitter:image"]', attr: 'content', create: () => { const el = document.createElement('meta'); el.setAttribute('name', 'twitter:image'); return el; } },
};

export function useHeadTags(tags) {
  const { alternates, ogLocale, robots } = tags;
  const values = Object.keys(TAG_CONFIG).map((key) => tags[key]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    Object.entries(TAG_CONFIG).forEach(([key, config]) => {
      const value = tags[key];
      if (value === undefined) return;
      let el = document.querySelector(config.selector);
      if (!el) {
        el = config.create();
        document.head.appendChild(el);
      }
      if (config.attr) {
        el.setAttribute(config.attr, value);
      } else {
        el.textContent = value;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, values);

  // Same find-or-create-once pattern for hreflang alternates: each one is
  // identified by its own `hreflang` attribute value, not a single selector,
  // since a page can render several (de/en/x-default). This is an SPA, so a
  // route that does not pass a given hreflang (or passes none at all) must
  // also remove any leftover tag from whichever route was previously
  // mounted — otherwise hreflang="de" from the German page would survive a
  // client-side navigation onto an English page.
  const alternatesList = alternates || [];
  const alternatesKey = alternatesList.map((a) => `${a.hrefLang}:${a.href}`).join('|');
  useLayoutEffect(() => {
    const allowed = new Set(alternatesList.map((a) => a.hrefLang));
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => {
      if (!allowed.has(el.getAttribute('hreflang'))) el.remove();
    });
    alternatesList.forEach(({ hrefLang, href }) => {
      let el = document.querySelector(`link[rel="alternate"][hreflang="${hrefLang}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hrefLang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alternatesKey]);

  // Same SPA leftover-tag problem for og:locale: only the German page sets
  // it, so every other route must remove it rather than leaving it stale.
  useLayoutEffect(() => {
    let el = document.querySelector('meta[property="og:locale"]');
    if (ogLocale === undefined) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', 'og:locale');
      document.head.appendChild(el);
    }
    el.setAttribute('content', ogLocale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ogLocale]);

  // Same SPA leftover-tag problem for robots: only a handful of noindex
  // pages set it, so every other route must remove it rather than leaving
  // a stale "noindex, follow" on an otherwise indexable page.
  useLayoutEffect(() => {
    let el = document.querySelector('meta[name="robots"]');
    if (robots === undefined) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', robots);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robots]);
}
